import api from './api';

// Types
export interface Call {
  id: string;
  officer_id: string;
  tenant_id: string;
  direction: 'inbound' | 'outbound';
  status: 'initiated' | 'ringing' | 'connected' | 'on_hold' | 'ended';
  mode: 'webrtc' | 'phone';
  from_number: string;
  to_number: string;
  forward_number?: string;
  started_at: string;
  connected_at?: string;
  ended_at?: string;
  duration_seconds?: number;
  call_control_id?: string;  // Provider-agnostic call control identifier
  conference_id?: string;
}

export interface CallParticipant {
  id: string;
  call_id: string;
  name: string;
  number: string;
  type: 'officer' | 'external' | 'internal';
  status: 'ringing' | 'connected' | 'on_hold' | 'disconnected';
  joined_at: string;
  left_at?: string;
  call_control_id?: string;  // Provider-agnostic call control identifier
}

export interface RecentCall {
  id: string;
  name: string;
  number: string;
  direction: 'inbound' | 'outbound';
  status: 'answered' | 'missed' | 'declined';
  time: string;
  duration?: number;
}

export interface QuickDialContact {
  id: string;
  name: string;
  number: string;
  short_label: string;
  is_extension: boolean;
}

export interface InitiateCallRequest {
  to_number: string;
  mode: 'webrtc' | 'phone';
  forward_to?: string;
}

// Incoming call from WebSocket notification
export interface IncomingCall {
  call_id: string;
  from_number: string;
  from_name?: string | null;
  to_number: string;
  direction: 'inbound';
}

// Response from answer-incoming endpoint
export interface AnswerIncomingResponse {
  status: string;
  call_id: string;
  conference_id: string;
  conference_name: string;
  sip_uri: string;
  from_number: string;
  from_name?: string | null;
  // Participant info from backend (for mute/hold/end controls)
  participant?: {
    id: string;
    call_control_id: string;
    name: string;
    number: string;
    type: string;
    status: string;
  };
}

export interface WebRTCCredentials {
  token: string;
  identity: string;
  expires_at: string;
  sip_uri: string;  // Officer's DID for conference-based calling
  caller_name: string | null;
  caller_number: string | null;
  ice_servers: Array<{ urls: string }>;
}

// Service
class DialerService {
  private baseUrl = '/api/v1/telephony/calls';

  // Call operations
  async initiateCall(request: InitiateCallRequest): Promise<Call> {
    const response = await api.post<Call>(`${this.baseUrl}/initiate`, request);
    return response.data;
  }

  async endCall(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/end`);
  }

  async hold(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/hold`);
  }

  async resume(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/resume`);
  }

  async mute(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/mute`);
  }

  async unmute(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/unmute`);
  }

  async transfer(callId: string, targetNumber: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/transfer`, { target_number: targetNumber });
  }

  async sendDTMF(callId: string, digit: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/dtmf`, { digit });
  }

  // Conference/Bridge operations
  async addParticipant(callId: string, number: string): Promise<CallParticipant> {
    const response = await api.post<CallParticipant>(`${this.baseUrl}/${callId}/participants`, { number });
    return response.data;
  }

  async removeParticipant(callId: string, participantId: string): Promise<{ message: string; participant_id: string; call_ended?: boolean }> {
    const response = await api.delete(`${this.baseUrl}/${callId}/participants/${participantId}`);
    return response.data;
  }

  // Individual participant controls
  async muteParticipant(callId: string, participantId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/participants/${participantId}/mute`);
  }

  async unmuteParticipant(callId: string, participantId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/participants/${participantId}/unmute`);
  }

  async holdParticipant(callId: string, participantId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/participants/${participantId}/hold`);
  }

  async resumeParticipant(callId: string, participantId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/participants/${participantId}/resume`);
  }

  // Conference-wide controls
  async muteAll(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/mute-all`);
  }

  async unmuteAll(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/unmute-all`);
  }

  async holdAll(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/hold-all`);
  }

  async resumeAll(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/resume-all`);
  }

  // WebRTC signaling
  async sendAnswer(callId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/webrtc/answer`, { answer });
  }

  async sendIceCandidate(callId: string, candidate: RTCIceCandidate): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/webrtc/ice-candidate`, { candidate });
  }

  // Recent calls
  async getRecentCalls(limit: number = 20): Promise<RecentCall[]> {
    const response = await api.get<RecentCall[]>(`${this.baseUrl}/recent`, { params: { limit } });
    return response.data;
  }

  // Quick dial contacts (from contacts marked as quick dial)
  async getQuickDialContacts(): Promise<QuickDialContact[]> {
    const response = await api.get<QuickDialContact[]>(`${this.baseUrl}/quick-dial`);
    return response.data;
  }

  async addToQuickDial(contactId: string): Promise<QuickDialContact> {
    const response = await api.post<QuickDialContact>(`${this.baseUrl}/quick-dial/${contactId}`);
    return response.data;
  }

  async removeFromQuickDial(contactId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/quick-dial/${contactId}`);
  }

  // Get call by ID
  async getCall(callId: string): Promise<Call> {
    const response = await api.get<Call>(`${this.baseUrl}/${callId}`);
    return response.data;
  }

  // Get active call
  async getActiveCall(): Promise<Call | null> {
    try {
      const response = await api.get<Call>(`${this.baseUrl}/active`);
      return response.data;
    } catch {
      return null;
    }
  }

  // Get WebRTC credentials for browser-based calling
  async getWebRTCCredentials(): Promise<WebRTCCredentials> {
    const response = await api.get<WebRTCCredentials>(`${this.baseUrl}/webrtc/credentials`);
    return response.data;
  }

  // Incoming call operations
  /**
   * Answer an incoming call and get conference info for WebRTC bridge.
   * Call this when officer clicks "Answer" on incoming call notification.
   */
  async answerIncoming(callId: string): Promise<AnswerIncomingResponse> {
    const response = await api.post<AnswerIncomingResponse>(`${this.baseUrl}/${callId}/answer-incoming`);
    return response.data;
  }

  /**
   * Reject an incoming call.
   * Call this when officer clicks "Decline" on incoming call notification.
   */
  async rejectIncoming(callId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${callId}/reject-incoming`);
  }
}

export const dialerService = new DialerService();
export default dialerService;
